import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Mic,
  MicOff,
  RotateCcw,
  X,
  Loader2,
  Volume2,
  ArrowLeft,
  Send,
  Zap,
  Hand,
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + '/api';

const FaceToFaceMode = () => {
  const navigate = useNavigate();
  const { token, user } = useApp();
  
  // Each person just sets what language they READ (their native language)
  const [myLang, setMyLang] = useState('pl'); // Bottom person reads in this language
  const [partnerLang, setPartnerLang] = useState('de'); // Top person reads in this language
  
  // Active speaker (1 = bottom, 2 = top)
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [autoSend, setAutoSend] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  
  // Current speech
  const [currentText, setCurrentText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  
  // Conversation history
  const [messages, setMessages] = useState([]);
  
  // Speech recognition ref
  const recognitionRef = useRef(null);
  const pendingTextRef = useRef('');
  const silenceTimerRef = useRef(null);
  const SILENCE_DELAY = 2000; // 2 seconds pause before translating
  
  // Session for saving
  const [sessionId, setSessionId] = useState(null);
  
  // All supported languages
  const ALL_LANGUAGES = {
    pl: { name: 'Polski', flag: '🇵🇱', code: 'pl-PL' },
    de: { name: 'Deutsch', flag: '🇩🇪', code: 'de-DE' },
    en: { name: 'English', flag: '🇬🇧', code: 'en-US' },
    es: { name: 'Español', flag: '🇪🇸', code: 'es-ES' },
    fr: { name: 'Français', flag: '🇫🇷', code: 'fr-FR' },
    it: { name: 'Italiano', flag: '🇮🇹', code: 'it-IT' },
    uk: { name: 'Українська', flag: '🇺🇦', code: 'uk-UA' },
    ru: { name: 'Русский', flag: '🇷🇺', code: 'ru-RU' },
    zh: { name: '中文', flag: '🇨🇳', code: 'zh-CN' },
    ja: { name: '日本語', flag: '🇯🇵', code: 'ja-JP' },
    ko: { name: '한국어', flag: '🇰🇷', code: 'ko-KR' },
    ar: { name: 'العربية', flag: '🇸🇦', code: 'ar-SA' },
    pt: { name: 'Português', flag: '🇵🇹', code: 'pt-PT' },
    nl: { name: 'Nederlands', flag: '🇳🇱', code: 'nl-NL' },
    tr: { name: 'Türkçe', flag: '🇹🇷', code: 'tr-TR' },
    cs: { name: 'Čeština', flag: '🇨🇿', code: 'cs-CZ' },
  };
  
  // Create session on mount
  useEffect(() => {
    createSession();
  }, []);
  
  const createSession = async () => {
    try {
      const res = await fetch(`${API_BASE}/live/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ language1: myLang, language2: partnerLang })
      });
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
      }
    } catch (e) {
      console.error('Failed to create session:', e);
    }
  };
  
  const startListening = (speaker) => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported');
      return;
    }
    
    // Use the speaker's language for better recognition, but we'll auto-detect anyway
    const listeningLang = speaker === 1 ? myLang : partnerLang;
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    // Set language hint but translation will auto-detect
    recognitionRef.current.lang = ALL_LANGUAGES[listeningLang]?.code || 'en-US';
    
    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Show accumulated + current text
      const displayText = pendingTextRef.current + (interimTranscript || finalTranscript);
      setCurrentText(displayText);
      
      if (finalTranscript) {
        // Accumulate text
        pendingTextRef.current += finalTranscript + ' ';
        
        // Auto-send mode: wait for silence then translate
        if (autoSend) {
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
          }
          
          silenceTimerRef.current = setTimeout(() => {
            const textToTranslate = pendingTextRef.current.trim();
            if (textToTranslate) {
              handleTranslate(textToTranslate, speaker);
              pendingTextRef.current = '';
              setCurrentText('');
            }
          }, SILENCE_DELAY);
        }
        // Manual mode: just accumulate, user will click send
      }
    };
    
    recognitionRef.current.onerror = (event) => {
      if (event.error !== 'no-speech') {
        console.error('Speech error:', event.error);
      }
    };
    
    recognitionRef.current.onend = () => {
      if (isListening && activeSpeaker === speaker) {
        recognitionRef.current.start();
      }
    };
    
    recognitionRef.current.start();
    setActiveSpeaker(speaker);
    setIsListening(true);
    pendingTextRef.current = '';
    setCurrentText('');
    setTranslatedText('');
  };
  
  const stopListening = () => {
    // Translate pending text before stopping
    const currentSpeaker = activeSpeaker;
    if (pendingTextRef.current.trim() && currentSpeaker) {
      handleTranslate(pendingTextRef.current.trim(), currentSpeaker);
      pendingTextRef.current = '';
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    setActiveSpeaker(null);
  };
  
  // Manual send - translate current accumulated text
  const sendMessage = () => {
    const currentSpeaker = activeSpeaker;
    if (pendingTextRef.current.trim() && currentSpeaker) {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      handleTranslate(pendingTextRef.current.trim(), currentSpeaker);
      pendingTextRef.current = '';
      setCurrentText('');
    }
  };
  
  const handleTranslate = async (text, speaker) => {
    // Target language = the OTHER person's reading language
    // Source = auto-detect
    const targetLang = speaker === 1 ? partnerLang : myLang;
    
    setIsTranslating(true);
    setCurrentText(text);
    
    try {
      const authToken = token || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/live/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({
          text: text,
          source_lang: 'auto', // Auto-detect source language
          target_lang: targetLang
        })
      });
      
      if (res.ok) {
        const { translated_text } = await res.json();
        setTranslatedText(translated_text);
        
        // Add to history
        const newMsg = {
          id: Date.now(),
          speaker: speaker,
          original: text,
          translated: translated_text,
          targetLang
        };
        setMessages(prev => [...prev, newMsg]);
        
        // Save to session
        if (sessionId) {
          try {
            const saveRes = await fetch(`${API_BASE}/live/session/${sessionId}/message`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` 
              },
              body: JSON.stringify({
                speaker: `Person ${speaker}`,
                original_text: text,
                translated_text: translated_text,
                source_lang: 'auto',
                target_lang: targetLang
              })
            });
            if (!saveRes.ok) {
              console.error('Failed to save message:', await saveRes.text());
            }
          } catch (saveErr) {
            console.error('Error saving message:', saveErr);
          }
        }
      }
    } catch (e) {
      toast.error('Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };
  
  const clearCurrent = () => {
    setCurrentText('');
    setTranslatedText('');
  };
  
  const endSession = async () => {
    stopListening();
    if (sessionId && messages.length > 0) {
      try {
        const res = await fetch(`${API_BASE}/live/session/${sessionId}/end`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        toast.success(`Session saved with ${messages.length} messages!`);
        // Navigate to summary page
        navigate(`/live/${sessionId}/summary`);
        return;
      } catch (e) {
        toast.error('Failed to save session');
      }
    }
    navigate('/live');
  };
  
  return (
    <div className="fixed inset-0 bg-slate-950 flex flex-col overflow-hidden">
      {/* TOP HALF - Rotated 180° for person across the table */}
      <div 
        className="flex-1 flex flex-col p-4"
        style={{ transform: 'rotate(180deg)' }}
      >
        {/* Person 2's display area */}
        <div className="flex-1 flex flex-col justify-between">
          {/* Their translation (what they read) */}
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center max-w-2xl">
              {activeSpeaker === 1 && translatedText && (
                <p className="text-4xl md:text-5xl font-bold text-white leading-relaxed">
                  {translatedText}
                </p>
              )}
              {activeSpeaker === 2 && currentText && (
                <p className="text-3xl md:text-4xl text-slate-300 leading-relaxed">
                  {currentText}
                </p>
              )}
              {!activeSpeaker && messages.length > 0 && (
                <p className="text-2xl text-slate-500">
                  {messages[messages.length - 1]?.translated}
                </p>
              )}
            </div>
          </div>
          
          {/* Person 2's controls */}
          <div className="flex items-center justify-center gap-4 pb-4">
            <div className="flex items-center gap-4">
              {/* Send button (manual mode) */}
              {!autoSend && activeSpeaker === 2 && currentText && (
                <Button
                  size="lg"
                  onClick={sendMessage}
                  className="h-16 w-16 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-xl"
                  data-testid="person2-send"
                >
                  <Send className="w-7 h-7" />
                </Button>
              )}
              
              <div className="text-center">
                <Button
                  size="lg"
                  onClick={() => activeSpeaker === 2 ? stopListening() : startListening(2)}
                  disabled={activeSpeaker === 1}
                  className={`h-20 w-20 rounded-full text-white shadow-2xl transition-all ${
                    activeSpeaker === 2 
                      ? 'bg-red-500 hover:bg-red-600 scale-110 recording-pulse' 
                      : activeSpeaker === 1 
                        ? 'bg-slate-700 cursor-not-allowed'
                        : 'bg-amber-500 hover:bg-amber-600'
                  }`}
                  data-testid="person2-mic"
                >
                  {activeSpeaker === 2 ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
                </Button>
                {/* Language selector for person 2 */}
                <button 
                  onClick={() => setShowSettings(!showSettings)}
                  className="mt-3 flex items-center justify-center gap-2 px-3 py-1 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors"
                >
                  <span className="text-2xl">{ALL_LANGUAGES[partnerLang]?.flag}</span>
                  <span className="text-white text-sm">{ALL_LANGUAGES[partnerLang]?.name}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* DIVIDER - Center line */}
      <div className="relative h-2 bg-gradient-to-r from-violet-600 via-purple-500 to-violet-600 flex items-center justify-center">
        <div className="absolute flex items-center gap-2 bg-slate-900 px-4 py-2 rounded-full border-2 border-violet-500">
          {isTranslating && <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />}
          <span className="text-xs text-violet-300 font-medium">
            {isTranslating ? 'Translating...' : `🌍 Universal Translator`}
          </span>
        </div>
        
        {/* Back button */}
        <button 
          onClick={endSession}
          className="absolute left-4 flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Exit & Save</span>
        </button>
        
        {/* Messages count */}
        <div className="absolute right-4 px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs">
          {messages.length} messages saved
        </div>
      </div>
      
      {/* BOTTOM HALF - Normal orientation for person on this side */}
      <div className="flex-1 flex flex-col p-4">
        {/* Person 1's display area */}
        <div className="flex-1 flex flex-col justify-between">
          {/* Person 1's controls */}
          <div className="flex items-center justify-center gap-4 pt-4">
            <div className="flex items-center gap-4">
              <div className="text-center">
                {/* Language selector for person 1 */}
                <button 
                  onClick={() => setShowSettings(!showSettings)}
                  className="mb-3 flex items-center justify-center gap-2 px-3 py-1 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors"
                >
                  <span className="text-2xl">{ALL_LANGUAGES[myLang]?.flag}</span>
                  <span className="text-white text-sm">{ALL_LANGUAGES[myLang]?.name}</span>
                </button>
                <Button
                  size="lg"
                  onClick={() => activeSpeaker === 1 ? stopListening() : startListening(1)}
                  disabled={activeSpeaker === 2}
                  className={`h-20 w-20 rounded-full text-white shadow-2xl transition-all ${
                    activeSpeaker === 1 
                      ? 'bg-red-500 hover:bg-red-600 scale-110 recording-pulse' 
                      : activeSpeaker === 2 
                        ? 'bg-slate-700 cursor-not-allowed'
                        : 'bg-violet-600 hover:bg-violet-700'
                  }`}
                  data-testid="person1-mic"
                >
                  {activeSpeaker === 1 ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
                </Button>
              </div>
              
              {/* Send button (manual mode) */}
              {!autoSend && activeSpeaker === 1 && currentText && (
                <Button
                  size="lg"
                  onClick={sendMessage}
                  className="h-16 w-16 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-xl"
                  data-testid="person1-send"
                >
                  <Send className="w-7 h-7" />
                </Button>
              )}
            </div>
            
            {/* Mode toggle */}
            <div className="absolute right-4 top-4">
              <button
                onClick={() => setAutoSend(!autoSend)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  autoSend 
                    ? 'bg-violet-600 text-white' 
                    : 'bg-slate-700 text-slate-300'
                }`}
                data-testid="mode-toggle"
              >
                {autoSend ? <Zap className="w-4 h-4" /> : <Hand className="w-4 h-4" />}
                <span className="text-sm font-medium">{autoSend ? 'Auto' : 'Manual'}</span>
              </button>
            </div>
          </div>
          
          {/* Their translation (what they read) */}
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="text-center max-w-2xl">
              {activeSpeaker === 2 && translatedText && (
                <p className="text-4xl md:text-5xl font-bold text-white leading-relaxed">
                  {translatedText}
                </p>
              )}
              {activeSpeaker === 1 && currentText && (
                <p className="text-3xl md:text-4xl text-slate-300 leading-relaxed">
                  {currentText}
                </p>
              )}
              {!activeSpeaker && messages.length > 0 && (
                <p className="text-2xl text-slate-500">
                  {messages[messages.length - 1]?.translated}
                </p>
              )}
              {!activeSpeaker && messages.length === 0 && (
                <p className="text-xl text-slate-600">
                  Tap your microphone • Speak any language
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Language Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowSettings(false)}>
          <div className="bg-slate-900 rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-6 text-center">Choose Your Languages</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Your language (bottom)</label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(ALL_LANGUAGES).map(([code, lang]) => (
                    <button
                      key={code}
                      onClick={() => setMyLang(code)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        myLang === code 
                          ? 'bg-violet-600 text-white' 
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <span className="text-xl block">{lang.flag}</span>
                      <span className="text-xs">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Partner's language (top)</label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(ALL_LANGUAGES).map(([code, lang]) => (
                    <button
                      key={code}
                      onClick={() => setPartnerLang(code)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        partnerLang === code 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <span className="text-xl block">{lang.flag}</span>
                      <span className="text-xs">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <Button 
              onClick={() => setShowSettings(false)} 
              className="w-full mt-6 rounded-full bg-violet-600 hover:bg-violet-700"
            >
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaceToFaceMode;
